import { ScrollingModule } from '@angular/cdk/scrolling';
import { CommonModule } from '@angular/common';
import { Component, computed, effect, inject, model, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { IconComponent, LoadingSpinnerComponent } from '@ui';
import { debounceTime, map, startWith } from 'rxjs';
import { SimpleProperty } from '../../interfaces/property.interface';
import { PropertiesService } from '../../services/properties/properties.service';

@Component({
  selector: 'core-property-multi-select',
  standalone: true,
  templateUrl: './property-multi-select.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    LoadingSpinnerComponent,
    IconComponent,
    ScrollingModule,
  ],
})
export class PropertyMultiSelectComponent implements OnInit {
  // Model inputs/outputs for two-way binding
  allProperties = model<boolean>(false);
  propertyIds = model<number[]>([]);

  private readonly propertiesService = inject(PropertiesService);

  // State management with signals
  readonly loading = signal<boolean>(true);
  readonly properties = signal<SimpleProperty[]>([]);
  readonly searchTerm = new FormControl('');

  // Convert search term to signal
  private readonly searchTermSignal = toSignal(
    this.searchTerm.valueChanges.pipe(
      startWith(''),
      debounceTime(300),
      map((term) => term?.toLowerCase() || '')
    ),
    { initialValue: '' }
  );

  // Computed properties
  readonly filteredProperties = computed(() => {
    const search = this.searchTermSignal();
    const allProps = this.properties();

    if (!search) {
      return allProps;
    }

    return this.filterProperties(allProps, search);
  });

  readonly totalProperties = computed(() => this.properties().length);
  readonly hasSearchBar = computed(() => this.totalProperties() > 7);

  readonly selectedCount = computed(() => {
    if (this.allProperties()) {
      return this.totalProperties();
    }
    return this.propertyIds().length;
  });

  readonly isPropertySelected = computed(() => {
    const selectedIds = this.propertyIds();
    return (propertyId: number) => selectedIds.includes(propertyId);
  });

  constructor() {
    // Effect to handle "All properties" toggle
    effect(
      () => {
        if (this.allProperties()) {
          // When "All properties" is checked, clear individual selections
          this.propertyIds.set([]);
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit(): void {
    this.propertiesService
      .getProperties({ transformer: 'simple', paginate: false })
      .pipe(map((res) => res.data))
      .subscribe((properties) => {
        this.loading.set(false);
        this.properties.set(properties);
      });
  }

  onAllPropertiesChange(checked: boolean): void {
    this.allProperties.set(checked);
  }

  onPropertyToggle(propertyId: number, checked: boolean): void {
    const currentIds = this.propertyIds();

    if (checked) {
      // Add property if not already selected
      if (!currentIds.includes(propertyId)) {
        this.propertyIds.set([...currentIds, propertyId]);
      }
    } else {
      // Remove property
      this.propertyIds.set(currentIds.filter((id) => id !== propertyId));
    }

    // If a property is selected/deselected, uncheck "All properties"
    if (this.allProperties()) {
      this.allProperties.set(false);
    }
  }

  private filterProperties(properties: SimpleProperty[], search: string): SimpleProperty[] {
    return properties.filter(
      (p) =>
        p.name.toLowerCase().includes(search) ||
        (p.location?.apt ?? '').toLowerCase().includes(search) ||
        (p.location?.street ?? '').toLowerCase().includes(search) ||
        (p.location?.city ?? '').toLowerCase().includes(search)
    );
  }

  trackByPropertyId(index: number, property: SimpleProperty): number {
    return property.id;
  }
}
